import React, { useState, useEffect } from 'react'
import { checkApiHealth } from '../../services/ApiService'
import LoadingSpinner from '../../components/client/LoadingSpinner'

const SuperAdminSettings = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [apiHealthData, setApiHealthData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState('All')

  useEffect(() => {
    if (isDropdownOpen && !apiHealthData) {
      fetchApiHealth()
    }
  }, [isDropdownOpen])

  const fetchApiHealth = async () => {
    try {
      setLoading(true)
      const result = await checkApiHealth()
      if (result?.success) {
        setApiHealthData(result.data)
      }
    } catch (error) {
      console.error('Error fetching API health:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return 'text-green-600 bg-green-100'
      case 'unhealthy':
        return 'text-red-600 bg-red-100'
      case 'unknown':
        return 'text-yellow-600 bg-yellow-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return '✓'
      case 'unhealthy':
        return '✗'
      case 'unknown':
        return '?'
      default:
        return '○'
    }
  }

  const categories = apiHealthData?.apis
    ? [...new Set(apiHealthData.apis.map(api => api.category))]
    : []

  const filteredApis = apiHealthData?.apis
    ? selectedCategory === 'All'
      ? apiHealthData.apis
      : apiHealthData.apis.filter(api => api.category === selectedCategory)
    : []

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Settings</h1>
      
      {/* API Health Monitor Dropdown */}
      {/* <div className="mb-6">
        <div className="relative">
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="w-full md:w-64 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-between"
          >
            <span>API Health Monitor</span>
            <span className="ml-2">{isDropdownOpen ? '▲' : '▼'}</span>
          </button>

          {isDropdownOpen && (
            <div className="absolute z-10 mt-2 w-full md:w-[800px] bg-white rounded-lg shadow-lg border border-gray-200 max-h-[600px] overflow-hidden">
              {apiHealthData && (
                <div className="p-4 bg-gray-50 border-b border-gray-200">
                  <div className="flex flex-wrap gap-4 mb-4">
                    <div className="flex-1 min-w-[150px]">
                      <div className="text-sm text-gray-600">Total APIs</div>
                      <div className="text-2xl font-bold">{apiHealthData.total_apis}</div>
                    </div>
                    <div className="flex-1 min-w-[150px]">
                      <div className="text-sm text-green-600">Healthy</div>
                      <div className="text-2xl font-bold text-green-600">
                        {apiHealthData.healthy_apis}
                      </div>
                    </div>
                    <div className="flex-1 min-w-[150px]">
                      <div className="text-sm text-red-600">Unhealthy</div>
                      <div className="text-2xl font-bold text-red-600">
                        {apiHealthData.unhealthy_apis}
                      </div>
                    </div>
                    <div className="flex-1 min-w-[150px]">
                      <div className="text-sm text-yellow-600">Unknown</div>
                      <div className="text-2xl font-bold text-yellow-600">
                        {apiHealthData.unknown_apis}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setSelectedCategory('All')}
                      className={`px-3 py-1 rounded text-sm ${
                        selectedCategory === 'All'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      All
                    </button>
                    {categories.map(category => (
                      <button
                        key={category}
                        onClick={() => setSelectedCategory(category)}
                        className={`px-3 py-1 rounded text-sm ${
                          selectedCategory === category
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="overflow-y-auto max-h-[450px]">
                {loading ? (
                  <div className="p-8 flex justify-center">
                    <LoadingSpinner />
                  </div>
                ) : filteredApis && filteredApis.length > 0 ? (
                  <table className="w-full">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Method
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Endpoint
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Category
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Response Time
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Message
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredApis.map((api, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                                api.status
                              )}`}
                            >
                              <span className="mr-1">{getStatusIcon(api.status)}</span>
                              {api.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-mono">
                              {api.method}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm font-mono text-gray-900">{api.endpoint}</div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-sm text-gray-600">{api.category}</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-sm text-gray-600">
                              {api.response_time}ms
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm text-gray-600 max-w-xs truncate" title={api.message}>
                              {api.message}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    No API data available
                  </div>
                )}
              </div>

              <div className="p-4 bg-gray-50 border-t border-gray-200">
                <button
                  onClick={fetchApiHealth}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Refreshing...' : 'Refresh Health Status'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div> */}
    </div>
  )
}

export default SuperAdminSettings